import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import type Moveable from 'react-moveable'

import { projectDataSelectors } from 'store/features/projectData/selectors'
import { editorSelectors } from 'store/features/editor/selectors'
import { isActiveMediaItemPendingSelector } from 'store/selectors'
import { dispatch } from 'store'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { usePlayheadOverlappingItems } from 'hooks/timeline'
import { convertMediaItemsToTimelineItems } from 'utils/timeline'
import { useTimelineContext } from 'context/TimelineContext'
import {
  EVENT_TYPES,
  useSubscribeToEvent
} from 'services/event-bus/useEventBus'
import { shouldRenderGeneratingMediaCanvasOverlay } from '../helpers'
import { IMediaItem } from 'types/media'
import { PositionedMediaControls } from '../../MediaControls/PositionedMediaControls'
import ZOrderContextMenu from '../MovableItemsLayer/ZOrderContextMenu'
import { ZOrderAction } from '../MovableItemsLayer/ZOrderContextMenu/types'
import { getZIndexUpdatesForAction } from '../MovableItemsLayer/ZOrderContextMenu/hooks/media-z-order'

import CanvasItemFrame from './CanvasItemFrame'
import * as Styled from './styled'
import { useDeselectCanvasItems } from './hooks'

interface ICanvasItemsLayerProps {
  canvasScale: number
}

const CanvasItemsLayer: React.FC<ICanvasItemsLayerProps> = ({
  canvasScale
}) => {
  useDeselectCanvasItems()

  const mediaItems = useSelector(projectDataSelectors.mediaItems)
  const activeTimelineItem = useSelector(editorSelectors.activeTimelineItem)
  const isAnyBuffering = useSelector(editorSelectors.isAnyBuffering)
  const isPending = useSelector(isActiveMediaItemPendingSelector)
  const { isPlaying, getCurrentTimeImmediate, onPause } = useTimelineContext()

  const isPlayingRef = useRef(isPlaying)
  isPlayingRef.current = isPlaying

  const pauseIfPlaying = useCallback(() => {
    if (isPlayingRef.current) {
      onPause()
    }
  }, [onPause])
  const { width: canvasWidth, height: canvasHeight } = useSelector(
    projectDataSelectors.defaultDimensions
  )

  const [zOrderMenu, setZOrderMenu] = useState<{
    itemId: string
    x: number
    y: number
  } | null>(null)

  const targetRefsMap = useRef(new Map<string, HTMLDivElement>())
  const overlayRef = useRef<HTMLDivElement>(null)
  const activeMoveableRef = useRef<Moveable | null>(null)

  const timelineItems = useMemo(
    () => convertMediaItemsToTimelineItems(mediaItems),
    [mediaItems]
  )

  const [, setGenerationProgressVersion] = useState(0)
  const bumpGenerationProgressVersion = useCallback(() => {
    setGenerationProgressVersion(v => v + 1)
  }, [])
  useSubscribeToEvent(
    EVENT_TYPES.GENERATION_PROGRESS_UPDATE,
    bumpGenerationProgressVersion
  )

  const openZOrderMenu = useCallback(
    (args: { itemId: string; x: number; y: number }) => {
      setZOrderMenu(args)
    },
    []
  )

  const handleZOrderSelect = useCallback(
    (action: ZOrderAction) => {
      if (!zOrderMenu) {
        return
      }
      const updates = getZIndexUpdatesForAction(
        mediaItems,
        zOrderMenu.itemId,
        action
      )
      if (!updates || updates.length === 0) {
        return
      }
      dispatch(
        projectDataThunks.updateMediaItems(
          updates.map(({ id, zIndex }) => ({ id, updates: { zIndex } }))
        )
      )
    },
    [mediaItems, zOrderMenu]
  )

  const handleOpenZOrderFromToolbar = useCallback(
    (pos: { x: number; y: number }) => {
      if (!activeTimelineItem.id) {
        return
      }
      setZOrderMenu({
        itemId: activeTimelineItem.id,
        x: pos.x,
        y: pos.y
      })
    },
    [activeTimelineItem.id]
  )

  const visibleMediaIds = usePlayheadOverlappingItems({
    isAnyItemBuffering: isAnyBuffering,
    timelineItems
  })

  const mediaItemsById = useMemo(() => {
    const byId = new Map<string, IMediaItem>()
    for (const m of mediaItems) {
      byId.set(m.id, m)
    }
    return byId
  }, [mediaItems])

  /** Base z-index for the media controls host so it paints above every clip. */
  const mediaControlsHostZIndex = useMemo(() => {
    const maxZIndex = mediaItems.reduce(
      (acc, m) => Math.max(acc, m.zIndex ?? 0),
      0
    )
    return maxZIndex + 1
  }, [mediaItems])

  const frameElements: React.ReactElement[] = []
  let mediaControlsItem: IMediaItem | null = null

  for (const item of mediaItems) {
    const isVisible = visibleMediaIds.includes(item.id)
    const isGenerating = shouldRenderGeneratingMediaCanvasOverlay(item)

    const currentTime = getCurrentTimeImmediate() * 1000
    const isTimeVisible =
      currentTime >= item.startTime * 1000 &&
      currentTime < (item.startTime + item.duration) * 1000
    const hasNonZeroSize = item.width > 0 && item.height > 0
    const isMovableVisible = isTimeVisible && hasNonZeroSize

    const isActive = activeTimelineItem.id === item.id
    const isActiveMediaVisible = isActive && isVisible
    const isMediaControlsVisible =
      isActiveMediaVisible && !isPlaying && !isPending
    const isSelectionChromeVisible =
      isActive && isMovableVisible && !isPlaying && !isPending

    if (isMediaControlsVisible) {
      mediaControlsItem = item
    }
    if (!isMovableVisible) {
      continue
    }

    frameElements.push(
      <CanvasItemFrame
        key={item.id}
        item={item}
        mediaItemsById={mediaItemsById}
        isVisible={isVisible}
        canvasScale={canvasScale}
        isGenerating={isGenerating}
        isActive={isActive}
        isMovableVisible={isMovableVisible}
        isSelectionChromeVisible={isSelectionChromeVisible}
        targetRefsMap={targetRefsMap}
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
        onOpenZOrderMenu={openZOrderMenu}
        activeTimelineItemId={activeTimelineItem.id || null}
        activeMoveableRef={activeMoveableRef}
        pauseIfPlaying={pauseIfPlaying}
      />
    )
  }

  return (
    <Styled.LayerOverlay ref={overlayRef}>
      <Styled.MoveableHandleStyles />
      {frameElements}
      {mediaControlsItem ? (
        <PositionedMediaControls
          isVisible
          item={mediaControlsItem}
          itemId={mediaControlsItem.id}
          displayWidth={mediaControlsItem.width}
          displayHeight={mediaControlsItem.height}
          hostZIndex={mediaControlsHostZIndex}
          targetRefsMap={targetRefsMap}
          overlayRef={overlayRef}
          canvasScale={canvasScale}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          onOpenZOrderMenuFromToolbar={handleOpenZOrderFromToolbar}
        />
      ) : null}
      {zOrderMenu ? (
        <ZOrderContextMenu
          position={{ x: zOrderMenu.x, y: zOrderMenu.y }}
          onClose={() => setZOrderMenu(null)}
          onSelect={handleZOrderSelect}
        />
      ) : null}
    </Styled.LayerOverlay>
  )
}

export default CanvasItemsLayer
